import type {
  AllMembershipPlanType,
  MarketedMembershipPlan,
  MarketedMembershipPlanType,
  MembershipPlanShape,
} from '@frontend/api-client';
import { getMembershipPlanDisplayPricing } from '@frontend/api-client';
import { usePaywallContext } from '@frontend/react-domain';
import { type PricingState, getValidatedPromoInput, useMembershipPlans } from '@frontend/web/hooks';
import { createContext, useContext, useEffect, useMemo } from 'react';

type PaywallPricingContextValue = {
  activePromoCode?: string;
  isError: boolean;
  isLoading: boolean;
  planLookup: Partial<Record<AllMembershipPlanType, MembershipPlanShape>>;
  pricingError?: 'planTiersFailed' | 'promoPricingFailed';
  pricingState: PricingState;
  refetch: () => void;
  selectablePlans: Array<MembershipPlanShape & { planType: MarketedMembershipPlanType }>;
  selectedPlanData?: MarketedMembershipPlan;
  selectedPlanPricing?: ReturnType<typeof getMembershipPlanDisplayPricing>;
};

const PaywallPricingContext = createContext<PaywallPricingContextValue | undefined>(undefined);

export const usePaywallPricingContext = () => {
  const context = useContext(PaywallPricingContext);

  if (!context) {
    throw new Error('usePaywallPricingContext must be used within PaywallPricingProvider');
  }

  return context;
};

export const PaywallPricingProvider = ({ children }: { children: React.ReactNode }) => {
  const { paywallActions, paywallState } = usePaywallContext();

  const validatedPromo = getValidatedPromoInput({
    promoCode: paywallState.promotionTranslation.promoCode,
    promotionId: paywallState.promotionTranslation.promotionDetails?.promotionId,
  });

  const membershipPlans = useMembershipPlans({ validatedPromo });

  useEffect(() => {
    paywallActions.setPlanLookup(membershipPlans.displayPlanLookup);
  }, [membershipPlans.displayPlanLookup, paywallActions.setPlanLookup]);

  const selectedPlanData = useMemo(
    () =>
      paywallState.purchase.selectedPlanType
        ? (membershipPlans.displayPlanLookup[paywallState.purchase.selectedPlanType] as
            | MarketedMembershipPlan
            | undefined)
        : undefined,
    [membershipPlans.displayPlanLookup, paywallState.purchase.selectedPlanType],
  );

  const selectedPlanPricing = useMemo(() => getMembershipPlanDisplayPricing(selectedPlanData), [selectedPlanData]);

  const value = useMemo(
    () =>
      ({
        activePromoCode: membershipPlans.activePromoCode,
        isError: membershipPlans.isError,
        isLoading: membershipPlans.isLoading,
        planLookup: membershipPlans.displayPlanLookup,
        pricingError: membershipPlans.pricingError,
        pricingState: membershipPlans.pricingState,
        refetch: membershipPlans.refetch,
        selectablePlans: membershipPlans.displaySelectablePlans,
        selectedPlanData,
        selectedPlanPricing,
      }) satisfies PaywallPricingContextValue,
    [
      membershipPlans.activePromoCode,
      membershipPlans.displayPlanLookup,
      membershipPlans.displaySelectablePlans,
      membershipPlans.isError,
      membershipPlans.isLoading,
      membershipPlans.pricingError,
      membershipPlans.pricingState,
      membershipPlans.refetch,
      selectedPlanData,
      selectedPlanPricing,
    ],
  );

  return <PaywallPricingContext.Provider value={value}>{children}</PaywallPricingContext.Provider>;
};
